(() => {
  if (customElements.get('mc-happy-customers')) return;

  class MCHappyCustomers extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.viewport = this.querySelector('[data-mc-customer-viewport]');
      this.track = this.querySelector('[data-mc-customer-track]');
      this.pagination = this.querySelector('[data-mc-customer-pagination]');
      this.dotsHost = this.querySelector('[data-mc-customer-dots]');
      this.status = this.querySelector('[data-mc-customer-status]');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.originalItems = this.track
        ? Array.from(this.track.querySelectorAll(':scope > [data-mc-customer-item]'))
        : [];

      this.pageSize = 1;
      this.pageCount = 1;
      this.currentPage = 0;
      this.isJumping = false;
      this.buildToken = 0;
      this.autoplayTimer = null;
      this.resumeTimer = null;
      this.scrollTimer = null;
      this.resizeTimer = null;

      if (!this.viewport || !this.track || this.originalItems.length === 0) return;

      this.addEventListener('click', this.handleClick);
      this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
      this.viewport.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
      this.viewport.addEventListener('pointerup', this.handlePointerUp, { passive: true });
      this.viewport.addEventListener('pointercancel', this.handlePointerUp, { passive: true });
      this.viewport.addEventListener('touchend', this.handlePointerUp, { passive: true });
      this.viewport.addEventListener('wheel', this.handleWheel, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.handleBreakpointChange);
      } else {
        this.mobileQuery.addListener(this.handleBreakpointChange);
      }

      this.build(false);
    }

    disconnectedCallback() {
      this.stopAutoplay();
      clearTimeout(this.resumeTimer);
      clearTimeout(this.scrollTimer);
      clearTimeout(this.resizeTimer);

      this.removeEventListener('click', this.handleClick);
      this.viewport?.removeEventListener('scroll', this.handleScroll);
      this.viewport?.removeEventListener('pointerdown', this.handlePointerDown);
      this.viewport?.removeEventListener('pointerup', this.handlePointerUp);
      this.viewport?.removeEventListener('pointercancel', this.handlePointerUp);
      this.viewport?.removeEventListener('touchend', this.handlePointerUp);
      this.viewport?.removeEventListener('wheel', this.handleWheel);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);

      if (this.mobileQuery?.removeEventListener) {
        this.mobileQuery.removeEventListener('change', this.handleBreakpointChange);
      } else {
        this.mobileQuery?.removeListener(this.handleBreakpointChange);
      }

      this.initialized = false;
    }

    getPageSize() {
      const desktopSize = Math.max(1, Number(this.dataset.desktopItemsPerPage) || 4);
      const mobileSize = Math.max(1, Number(this.dataset.mobileItemsPerPage) || 1);
      return this.mobileQuery.matches ? mobileSize : desktopSize;
    }

    getPageWidth() {
      return this.viewport?.clientWidth || 0;
    }

    build(preservePosition = true) {
      const previousFirstItem = preservePosition ? this.currentPage * this.pageSize : 0;
      const token = ++this.buildToken;

      this.stopAutoplay();
      clearTimeout(this.resumeTimer);
      clearTimeout(this.scrollTimer);

      this.pageSize = this.getPageSize();
      this.pageCount = Math.max(1, Math.ceil(this.originalItems.length / this.pageSize));
      this.currentPage = Math.min(
        this.pageCount - 1,
        Math.floor(previousFirstItem / this.pageSize)
      );

      this.track.replaceChildren();
      this.pages = [];

      for (let pageIndex = 0; pageIndex < this.pageCount; pageIndex += 1) {
        const page = document.createElement('div');
        page.className = 'mc-happy-customers__page';
        page.dataset.mcCustomerPagePanel = String(pageIndex);
        page.setAttribute('role', 'group');
        page.setAttribute('aria-roledescription', 'slide');
        page.setAttribute('aria-label', `${pageIndex + 1} of ${this.pageCount}`);

        const start = pageIndex * this.pageSize;
        const end = Math.min(start + this.pageSize, this.originalItems.length);

        for (let itemIndex = start; itemIndex < end; itemIndex += 1) {
          page.append(this.originalItems[itemIndex]);
        }

        this.pages.push(page);
        this.track.append(page);
      }

      if (this.pageCount > 1) {
        const startClone = this.createClone(this.pages[this.pages.length - 1]);
        const endClone = this.createClone(this.pages[0]);
        this.track.prepend(startClone);
        this.track.append(endClone);
      }

      this.classList.add('is-ready');
      this.renderDots();
      this.updateDots(this.currentPage);

      requestAnimationFrame(() => {
        if (token !== this.buildToken || !this.isConnected) return;
        this.jumpToPage(this.currentPage);
        this.startAutoplay();
      });
    }

    createClone(page) {
      const clone = page.cloneNode(true);
      clone.dataset.mcCustomerClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('role');
      clone.removeAttribute('aria-roledescription');
      clone.removeAttribute('aria-label');

      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
        element.removeAttribute('data-shopify-editor-block');
      });
      clone.querySelectorAll('[data-mc-customer-item]').forEach((element) => {
        element.removeAttribute('data-mc-customer-item');
        element.removeAttribute('data-block-id');
      });
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });

      return clone;
    }

    renderDots() {
      if (!this.pagination || !this.dotsHost) return;

      this.dotsHost.replaceChildren();

      for (let index = 0; index < this.pageCount; index += 1) {
        const dot = document.createElement('button');
        dot.className = 'mc-happy-customers__pagination-dot';
        dot.type = 'button';
        dot.dataset.mcCustomerDot = String(index);
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
        this.dotsHost.append(dot);
      }

      this.pagination.hidden = this.pageCount <= 1;
    }

    updateDots(pageIndex) {
      this.querySelectorAll('[data-mc-customer-dot]').forEach((dot) => {
        const isActive = Number(dot.dataset.mcCustomerDot) === pageIndex;
        dot.classList.toggle('is-active', isActive);

        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (this.status) {
        this.status.textContent = `Slide ${pageIndex + 1} of ${this.pageCount}`;
      }
    }

    jumpToPage(pageIndex) {
      const width = this.getPageWidth();
      if (!width) return;

      const physicalPage = this.pageCount > 1 ? pageIndex + 1 : 0;
      const previousBehavior = this.viewport.style.scrollBehavior;
      this.isJumping = true;
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft = physicalPage * width;
      this.viewport.offsetHeight;
      this.viewport.style.scrollBehavior = previousBehavior;

      requestAnimationFrame(() => {
        this.isJumping = false;
      });
    }

    scrollToPage(pageIndex, behavior = 'smooth') {
      const width = this.getPageWidth();
      if (!width || this.pageCount <= 1) return;

      const normalizedPage = (pageIndex + this.pageCount) % this.pageCount;
      this.currentPage = normalizedPage;
      this.updateDots(normalizedPage);
      this.viewport.scrollTo({
        left: (normalizedPage + 1) * width,
        behavior: this.reducedMotion.matches ? 'auto' : behavior,
      });
    }

    settleAfterScroll() {
      if (this.isJumping || this.pageCount <= 1) return;

      const width = this.getPageWidth();
      if (!width) return;

      const physicalPage = Math.round(this.viewport.scrollLeft / width);

      if (physicalPage <= 0) {
        this.currentPage = this.pageCount - 1;
        this.updateDots(this.currentPage);
        this.jumpToPage(this.currentPage);
        return;
      }

      if (physicalPage >= this.pageCount + 1) {
        this.currentPage = 0;
        this.updateDots(0);
        this.jumpToPage(0);
        return;
      }

      this.currentPage = physicalPage - 1;
      this.updateDots(this.currentPage);
    }

    goNext() {
      if (this.pageCount <= 1) return;

      const width = this.getPageWidth();
      if (!width) return;

      if (this.currentPage === this.pageCount - 1) {
        this.currentPage = 0;
        this.updateDots(0);
        this.viewport.scrollTo({
          left: (this.pageCount + 1) * width,
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        });
        return;
      }

      this.scrollToPage(this.currentPage + 1);
    }

    startAutoplay() {
      this.stopAutoplay();

      if (
        this.pageCount <= 1 ||
        document.hidden ||
        this.reducedMotion.matches
      ) return;

      const delay = Math.max(1000, Number(this.dataset.autoplayDelay) || 4000);
      this.autoplayTimer = setInterval(() => this.goNext(), delay);
    }

    stopAutoplay() {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    scheduleResume() {
      clearTimeout(this.resumeTimer);
      const delay = Math.max(1000, Number(this.dataset.resumeDelay) || 6000);
      this.resumeTimer = setTimeout(() => this.startAutoplay(), delay);
    }

    pauseAndResume() {
      this.stopAutoplay();
      this.scheduleResume();
    }

    handleClick = (event) => {
      const dot = event.target.closest('[data-mc-customer-dot]');
      if (!dot || !this.contains(dot)) return;

      const pageIndex = Number(dot.dataset.mcCustomerDot);
      if (!Number.isInteger(pageIndex)) return;

      this.pauseAndResume();
      this.scrollToPage(pageIndex);
    };

    handleScroll = () => {
      if (this.isJumping) return;
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.settleAfterScroll(), 100);
    };

    handlePointerDown = () => {
      this.stopAutoplay();
      clearTimeout(this.resumeTimer);
    };

    handlePointerUp = () => {
      this.scheduleResume();
    };

    handleWheel = () => {
      this.stopAutoplay();
      this.scheduleResume();
    };

    handleVisibilityChange = () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    };

    handleResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.jumpToPage(this.currentPage), 120);
    };

    handleBreakpointChange = () => {
      this.build(true);
    };

    handleBlockSelect = (event) => {
      if (!this.contains(event.target)) return;

      const blockId = event.detail?.blockId || event.target.dataset?.blockId;
      if (!blockId) return;

      const selectedIndex = this.originalItems.findIndex(
        (item) => item.dataset.blockId === blockId
      );
      if (selectedIndex < 0) return;

      const pageIndex = Math.floor(selectedIndex / this.pageSize);
      this.pauseAndResume();
      this.scrollToPage(pageIndex, 'auto');
    };
  }

  customElements.define('mc-happy-customers', MCHappyCustomers);
})();
